import { Content } from "antd/es/layout/layout";
import Footer from "components/Footer/Footer";
import { routes } from "config/routes";
import { usePermissions } from "hooks/usePermissions";
import React, { useEffect, useRef } from "react";
import { Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { BiTransfer } from "react-icons/bi";
import { FaCalculator, FaLandmark, FaPercentage } from "react-icons/fa";
import useCoreStore from "views/hooks/useCoreStore";
import Accounting from "./components/Accounting";
import Sales from "./components/Sales";
import TransferToAccounts from "./components/TransferToAccounts";
import Vats from "./components/Vats";
import useAccountingApi from "./hooks/useAccountingApi";
import useAuthStore from "views/hooks/useAuthStore";

export default function AccountingTabs(): React.ReactElement {
  const { t } = useTranslation();
  const { canExport } = usePermissions();
  const companySelected = useCoreStore(state => state.companySelected);
  const user = useAuthStore(state => state.userData);
  const contentRef = useRef<HTMLDivElement>(null);
  const { getCompanyPlatform, platformData } = useAccountingApi();

  const canExportSetting = platformData?.canExport;
  const userIsClient = ["client"].includes(user?.userCategory?.slug ?? "");

  const pathSegments = window.location.pathname.split("/");
  const activeTabKey = pathSegments[pathSegments.length - 1];

  useEffect(() => {
    if (companySelected) {
      getCompanyPlatform({
        variables: {
          id: companySelected?.id,
        },
      });
    }
  }, [companySelected]);
  const handleOnChange = (key: string) => {
    routes.navigate(`/company/${companySelected?.id}/settings/accounting/${key}`);
  };

  const TABS_OPTIONS = [
    {
      key: "vats",
      label: (
        <span className="tab-icon-container">
          <FaPercentage />
          {t("VAT")}
        </span>
      ),
      children: <Vats />,
    },
    ...(userIsClient
      ? []
      : [
          {
            key: "sales",
            label: (
              <span className="tab-icon-container">
                <FaCalculator />
                {t("Sales")}
              </span>
            ),
            children: <Sales />,
          },
          {
            key: "account",
            label: (
              <span className="tab-icon-container">
                <FaLandmark />
                {t("Accounting")}
              </span>
            ),
            children: <Accounting />,
          },
        ]),
  ];

  if (canExport && canExportSetting) {
    TABS_OPTIONS.push({
      key: "export-history",
      label: (
        <span className="tab-icon-container">
          <BiTransfer />
          {t("Exports")}
        </span>
      ),
      children: <TransferToAccounts />,
    });
  }

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      const scrollTop = contentElement.scrollTop;
      const tabsNav = document.querySelector(".ant-tabs-nav");
      if (tabsNav) {
        if (scrollTop > 12) {
          tabsNav.classList.add("scrolled");
        } else {
          tabsNav.classList.remove("scrolled");
        }
      }
    };

    contentElement.addEventListener("scroll", handleScroll);

    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <Content className="layout-card-content__with-tabs" ref={contentRef}>
        <Tabs
          className="antd-newrai-tabs"
          defaultActiveKey={activeTabKey}
          items={TABS_OPTIONS}
          onChange={handleOnChange}
        />
      </Content>
      <Footer />
    </>
  );
}
